import React, { useState, useEffect, useRef } from 'react';
import { renderIcon } from '../../../utils/icons';
import { getPhaseEmoji } from '../../../utils/site-constants';
import { Card } from '../../../components/ui';
import api from '../../../utils/api';
import WscTabButton from './WscTabButton';
import WscChecklistTab from './WscChecklistTab';
import WscNotesTab from './WscNotesTab';
import WscFilesTab from './WscFilesTab';
import WscToggleSwitch from './WscToggleSwitch';

export default function WorkstreamCard({ ws, gate, users, canAdmin, isDark, theme, mobile, updateItem, deleteItem, siteId }) {
  const [activeTab, setActiveTab] = useState('checklist');
  const [newItemText, setNewItemText] = useState('');
  const [newItemRequired, setNewItemRequired] = useState(true);
  const [adding, setAdding] = useState(false);
  const [attachments, setAttachments] = useState({});
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [uploadingItemId, setUploadingItemId] = useState(null);
  const [filterItemId, setFilterItemId] = useState(null);
  const [showAssignDropdown, setShowAssignDropdown] = useState(false);
  const [assignUpdating, setAssignUpdating] = useState(false);
  const fileInputRef = useRef(null);
  const assignDropdownRef = useRef(null);

  const gateStatus = gate.status || 'active';
  const items = ws.items || [];
  const requiredItems = items.filter(i => i.is_required !== false);
  const requiredDone = requiredItems.filter(i => i.status === 'Complete').length;
  const totalDone = items.filter(i => i.status === 'Complete').length;
  const threshold = ws.gate_threshold || 100;
  const pct = requiredItems.length > 0 ? Math.round((requiredDone / requiredItems.length) * 100) : 100;

  const totalAttachmentCount = Object.values(attachments).reduce((sum, arr) => sum + (arr ? arr.length : 0), 0);

  async function fetchAttachments() {
    if (!items.length) return;
    setAttachmentsLoading(true);
    const token = localStorage.getItem('dlp_token');
    const result = {};
    try {
      await Promise.all(items.map(async (item) => {
        try {
          const res = await fetch('/api/items/' + item.id + '/attachments', {
            headers: { Authorization: 'Bearer ' + token }
          });
          if (res.ok) {
            const data = await res.json();
            result[item.id] = data.attachments || [];
          }
        } catch (e) { /* skip failed items */ }
      }));
      setAttachments(result);
    } catch (e) {
      console.error('Failed to fetch attachments:', e);
    }
    setAttachmentsLoading(false);
  }

  useEffect(() => {
    if (activeTab === 'files') {
      fetchAttachments();
    }
  }, [activeTab]);

  // Close assign dropdown on outside click
  useEffect(() => {
    if (!showAssignDropdown) return;
    function handleClickOutside(e) {
      if (assignDropdownRef.current && !assignDropdownRef.current.contains(e.target)) {
        setShowAssignDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showAssignDropdown]);

  async function handleAssign(userId) {
    setAssignUpdating(true);
    try {
      await api.put('/api/workstreams/' + ws.id, { assigned_to: userId });
      if (window.__dlpRefresh) window.__dlpRefresh();
    } catch (e) {
      console.error('Failed to update assignment:', e);
    }
    setAssignUpdating(false);
    setShowAssignDropdown(false);
  }

  async function handleUpload(itemId, file) {
    if (!file) return;
    setUploadingItemId(itemId);
    const token = localStorage.getItem('dlp_token');
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await fetch('/api/items/' + itemId + '/attachments', {
        method: 'POST',
        headers: { Authorization: 'Bearer ' + token },
        body: fd,
      });
      if (res.ok) {
        const data = await res.json();
        setAttachments(prev => ({
          ...prev,
          [itemId]: [data.attachment, ...(prev[itemId] || [])]
        }));
      }
    } catch (e) {
      console.error('Upload failed:', e);
    }
    setUploadingItemId(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  async function handleDeleteAttachment(itemId, attachmentId) {
    const token = localStorage.getItem('dlp_token');
    try {
      const res = await fetch('/api/items/' + itemId + '/attachments/' + attachmentId, {
        method: 'DELETE',
        headers: { Authorization: 'Bearer ' + token },
      });
      if (res.ok) {
        setAttachments(prev => ({
          ...prev,
          [itemId]: (prev[itemId] || []).filter(a => a.id !== attachmentId)
        }));
      }
    } catch (e) {
      console.error('Delete failed:', e);
    }
  }

  function formatFileSize(bytes) {
    if (!bytes) return '0 B';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }

  const thresholdMet = pct >= threshold;
  const isComplete = gateStatus === 'complete' || (gateStatus === 'active' && thresholdMet && requiredItems.length > 0 && requiredDone === requiredItems.length);
  const isLocked = gateStatus === 'locked';
  const isActive = gateStatus === 'active' && !isComplete;

  const borderColor = isComplete ? '#4ADE80' : isActive ? '#4ADE80' : '#6b7280';
  const cardOpacity = isLocked ? 0.7 : 1;

  const assignee = users?.find(u => u.id === ws.assigned_to);

  const dueDateStr = ws.due_date
    ? new Date(ws.due_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : null;

  let footerBg, footerText;
  if (isComplete || gateStatus === 'complete') {
    footerBg = 'rgba(74,222,128,0.12)';
    footerText = 'Threshold met — gate cleared, ready to advance';
  } else if (isActive && thresholdMet) {
    footerBg = 'rgba(245,158,11,0.12)';
    footerText = 'Threshold met (' + pct + '%) — ready to advance';
  } else if (isActive) {
    const remaining = requiredItems.length > 0 ? Math.ceil((threshold / 100) * requiredItems.length) - requiredDone : 0;
    footerBg = 'rgba(245,158,11,0.12)';
    footerText = 'Need ' + threshold + '% — at ' + pct + '% (~' + Math.max(remaining, 0) + ' more required items)';
  } else {
    footerBg = 'rgba(239,68,68,0.10)';
    footerText = 'Gate locked — previous phase must reach its threshold first';
  }

  const progressColor = isComplete ? '#4ADE80' : isActive ? '#4ADE80' : '#6b7280';

  const boxShadow = isActive
    ? '0 0 20px rgba(59,130,246,0.15), 0 4px 16px rgba(0,0,0,0.3)'
    : isComplete
      ? '0 4px 16px rgba(0,0,0,0.3)'
      : 'none';

  async function handleAddItem() {
    if (!newItemText.trim() || adding) return;
    setAdding(true);
    try {
      const token = localStorage.getItem('dlp_token');
      const res = await fetch('/api/sites/' + siteId + '/workstreams/' + ws.id + '/items', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
        body: JSON.stringify({ doc: newItemText.trim(), priority: 'Medium', is_required: newItemRequired }),
      });
      if (res.ok) {
        setNewItemText('');
        setNewItemRequired(true);
        if (window.__dlpRefresh) window.__dlpRefresh();
      }
    } catch (e) {
      console.error('Failed to add item:', e);
    }
    setAdding(false);
  }

  const headerBg = isComplete
    ? 'rgba(74,222,128,0.06)'
    : isActive
      ? 'rgba(96,165,250,0.04)'
      : 'transparent';

  return (
    <Card style={{
      minWidth: mobile ? '100%' : 320,
      maxWidth: mobile ? '100%' : 360,
      flex: mobile ? '1' : '0 0 320px',
      opacity: cardOpacity,
      borderLeft: '3px solid ' + borderColor,
      boxShadow: boxShadow,
      padding: 0,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{ padding: '16px 16px 12px', background: headerBg }}>
        {/* Row 1: Title + Status badge */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
          <span style={{ fontSize: 20 }}>{getPhaseEmoji(ws.phase, 20)}</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 400, fontFamily: "'DM Sans', sans-serif", letterSpacing: '-0.01em', color: theme.text, lineHeight: 1.3 }}>
              {ws.name}
            </div>
          </div>
          <span style={{
            fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 12,
            background: borderColor + '22', color: borderColor,
            textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap', flexShrink: 0,
          }}>
            {isComplete ? 'COMPLETE' : isActive ? 'ACTIVE' : 'LOCKED'}
          </span>
        </div>

        {/* Row 2: Prominent Assignment Badge */}
        <div style={{ position: 'relative', marginBottom: 10 }} ref={assignDropdownRef}>
          <button
            onClick={() => { if (canAdmin && !isLocked) setShowAssignDropdown(!showAssignDropdown); }}
            style={{
              display: 'flex', alignItems: 'center', gap: 8, width: '100%',
              padding: '8px 12px', borderRadius: 10,
              background: assignee
                ? (isDark ? 'rgba(59,130,246,0.12)' : 'rgba(59,130,246,0.08)')
                : (isDark ? 'rgba(245,158,11,0.12)' : 'rgba(245,158,11,0.08)'),
              border: '1.5px solid ' + (assignee
                ? (isDark ? 'rgba(59,130,246,0.3)' : 'rgba(59,130,246,0.2)')
                : (isDark ? 'rgba(245,158,11,0.3)' : 'rgba(245,158,11,0.2)')),
              cursor: (canAdmin && !isLocked) ? 'pointer' : 'default',
              transition: 'all 0.15s ease',
              outline: 'none',
              textAlign: 'left',
            }}
          >
            {assignee ? (
              <span style={{
                width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                background: assignee.avatar_color || '#4ADE80',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                color: '#fff', fontSize: 13, fontWeight: 700,
                boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
              }}>
                {assignee.name?.charAt(0)?.toUpperCase() || '?'}
              </span>
            ) : (
              <span style={{
                width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                background: isDark ? 'rgba(245,158,11,0.2)' : 'rgba(245,158,11,0.15)',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                border: '1.5px dashed ' + (isDark ? 'rgba(245,158,11,0.5)' : 'rgba(245,158,11,0.4)'),
              }}>
                {renderIcon('user-plus', 14, '#f59e0b')}
              </span>
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8,
                color: theme.textSecondary, marginBottom: 1,
              }}>
                Assigned To
              </div>
              <div style={{
                fontSize: 14, fontWeight: 600,
                color: assignee ? theme.text : '#f59e0b',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {assignee ? assignee.name : 'Unassigned'}
              </div>
            </div>
            {canAdmin && !isLocked && (
              <span style={{ color: theme.textSecondary, flexShrink: 0 }}>
                {renderIcon('chevron-down', 16, 'currentColor')}
              </span>
            )}
          </button>

          {/* Assignment Dropdown */}
          {showAssignDropdown && (
            <div style={{
              position: 'absolute', top: '100%', left: 0, right: 0,
              zIndex: 50, marginTop: 4, borderRadius: 10,
              background: theme.bgCard,
              border: '1px solid ' + theme.border,
              boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
              maxHeight: 220, overflowY: 'auto',
            }}>
              {/* Unassign option */}
              {assignee && (
                <button
                  onClick={() => handleAssign(null)}
                  disabled={assignUpdating}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                    padding: '10px 12px', border: 'none', cursor: 'pointer',
                    background: 'transparent', textAlign: 'left',
                    borderBottom: '1px solid ' + theme.border,
                    color: '#EF4444', fontSize: 13, fontWeight: 500,
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  {renderIcon('x-circle', 16, '#EF4444')}
                  <span>Unassign</span>
                </button>
              )}
              {/* User list */}
              {(users || []).map(u => (
                <button
                  key={u.id}
                  onClick={() => handleAssign(u.id)}
                  disabled={assignUpdating}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                    padding: '10px 12px', border: 'none', cursor: 'pointer',
                    background: u.id === ws.assigned_to
                      ? (isDark ? 'rgba(59,130,246,0.1)' : 'rgba(59,130,246,0.06)')
                      : 'transparent',
                    textAlign: 'left', fontSize: 13,
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
                  onMouseLeave={e => e.currentTarget.style.background = u.id === ws.assigned_to
                    ? 'rgba(59,130,246,0.1)'
                    : 'transparent'}
                >
                  <span style={{
                    width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
                    background: u.avatar_color || '#6b7280',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    color: '#fff', fontSize: 11, fontWeight: 700,
                  }}>
                    {u.name?.charAt(0)?.toUpperCase() || '?'}
                  </span>
                  <span style={{ color: theme.text, fontWeight: u.id === ws.assigned_to ? 600 : 400 }}>
                    {u.name}
                  </span>
                  {u.id === ws.assigned_to && (
                    <span style={{ marginLeft: 'auto', color: '#4ADE80' }}>
                      {renderIcon('check', 14, '#4ADE80')}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {ws.summary && (
          <div style={{
            fontSize: 13, color: theme.textSecondary, lineHeight: 1.4,
            marginBottom: 10,
            overflow: 'hidden', display: '-webkit-box',
            WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
          }}>
            {ws.summary}
          </div>
        )}

        {dueDateStr && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: theme.textSecondary }}>
            {renderIcon('calendar', 13, 'currentColor', { marginRight: 2 })} Due: {dueDateStr}
          </div>
        )}
      </div>

      {/* Progress */}
      <div style={{ padding: '0 16px 12px' }}>
        <div style={{ fontSize: 11, color: theme.textSecondary, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>
          Gate: {threshold}% required · {requiredDone}/{requiredItems.length} required done
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{
            flex: 1, height: 6, borderRadius: 3,
            background: 'rgba(255,255,255,0.10)',
            overflow: 'hidden',
          }}>
            <div style={{
              width: pct + '%', height: '100%', borderRadius: 3,
              background: progressColor,
              transition: 'width 0.3s ease',
            }} />
          </div>
          <span style={{ fontSize: 12, fontWeight: 600, color: progressColor, whiteSpace: 'nowrap' }}>
            {pct}%
          </span>
        </div>
        <div style={{ fontSize: 11, color: theme.textSecondary, marginTop: 3 }}>
          {totalDone}/{items.length} total items done
        </div>
      </div>

      {/* Tab buttons */}
      <div style={{ display: 'flex', gap: 4, padding: '0 16px 8px' }}>
        <WscTabButton active={activeTab === 'checklist'} onClick={() => setActiveTab('checklist')} isDark={isDark} theme={theme} color={progressColor}>
          {renderIcon('check-square', 14)} Checklist
        </WscTabButton>
        <WscTabButton active={activeTab === 'notes'} onClick={() => setActiveTab('notes')} isDark={isDark} theme={theme} color={progressColor}>
          {renderIcon('file-text', 14)} Notes
        </WscTabButton>
        <WscTabButton active={activeTab === 'files'} onClick={() => { setFilterItemId(null); setActiveTab('files'); }} isDark={isDark} theme={theme} color={progressColor}>
          {renderIcon('paperclip', 14)} Files{totalAttachmentCount > 0 ? ' (' + totalAttachmentCount + ')' : ''}
        </WscTabButton>
      </div>

      {/* Tab content */}
      <div style={{
        flex: 1, overflowY: 'auto', padding: '0 16px 8px',
        maxHeight: 280, minHeight: 120,
        pointerEvents: isLocked ? 'none' : 'auto',
      }}>
        {activeTab === 'checklist' ? (
          <WscChecklistTab
            items={items}
            gateStatus={gateStatus}
            canAdmin={canAdmin}
            isDark={isDark}
            theme={theme}
            updateItem={updateItem}
            deleteItem={deleteItem}
            isLocked={isLocked}
            isActive={isActive || isComplete}
            attachments={attachments}
            users={users}
            onPaperclipClick={(itemId) => { setFilterItemId(itemId); setActiveTab('files'); fetchAttachments(); }}
          />
        ) : activeTab === 'notes' ? (
          <WscNotesTab items={items} theme={theme} isDark={isDark} />
        ) : (
          <WscFilesTab
            items={items}
            attachments={attachments}
            attachmentsLoading={attachmentsLoading}
            canAdmin={canAdmin}
            isDark={isDark}
            theme={theme}
            uploadingItemId={uploadingItemId}
            filterItemId={filterItemId}
            onClearFilter={() => setFilterItemId(null)}
            onUpload={(itemId) => { setUploadingItemId(itemId); fileInputRef.current && fileInputRef.current.click(); }}
            onFileSelected={(itemId, file) => handleUpload(itemId, file)}
            onDelete={handleDeleteAttachment}
            fileInputRef={fileInputRef}
            formatFileSize={formatFileSize}
            siteId={siteId}
            onDriveLinked={(itemId, att) => { setAttachments(prev => ({ ...prev, [itemId]: [att, ...(prev[itemId] || [])] })); }}
          />
        )}
      </div>

      {/* Add item */}
      {canAdmin && !isLocked && activeTab === 'checklist' && (
        <div style={{
          padding: '8px 16px', borderTop: '1px solid ' + theme.border,
          display: 'flex', gap: 6, alignItems: 'center',
        }}>
          <input
            value={newItemText}
            onChange={e => setNewItemText(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleAddItem(); }}
            placeholder="Add checklist item..."
            style={{
              flex: 1, padding: '6px 10px', fontSize: 12, borderRadius: 6,
              border: '1px solid ' + theme.border,
              background: 'rgba(255,255,255,0.03)',
              color: theme.text, outline: 'none',
            }}
          />
          <label style={{
            display: 'flex', alignItems: 'center', gap: 4, fontSize: 11,
            color: theme.textSecondary, cursor: 'pointer', whiteSpace: 'nowrap',
          }}>
            <WscToggleSwitch checked={newItemRequired} onChange={v => setNewItemRequired(v)} isDark={isDark} />
            Req
          </label>
          <button
            onClick={handleAddItem}
            disabled={adding || !newItemText.trim()}
            style={{
              padding: '5px 10px', borderRadius: 6, border: 'none',
              background: '#4ADE80', color: '#fff', fontSize: 12,
              fontWeight: 600, cursor: adding ? 'wait' : 'pointer',
              opacity: (!newItemText.trim() || adding) ? 0.5 : 1,
            }}
          >
            + Add
          </button>
        </div>
      )}

      {/* Footer */}
      <div style={{
        padding: '10px 16px', fontSize: 12, fontWeight: 500,
        background: footerBg,
        color: isComplete || gateStatus === 'complete' ? '#4ADE80'
             : isLocked ? '#EF4444'
             : '#F59E0B',
        borderTop: '1px solid ' + theme.border,
        lineHeight: 1.4,
      }}>
        {footerText}
      </div>
    </Card>
  );
}
